import React from 'react';
import {
  Box,
  Button,
  Typography,
  Tooltip,
  CircularProgress,
  Chip,
  Stack,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import { SectionTitle, ButtonContainer } from '../UserSettingsStyles';
import MortgageForm from './MortgageForm';
import MortgageView from './MortgageView';
import MortgageTrack from './MortgageTrack';
import NotificationAlert from '../NotificationAlert';

// Common button styles
const commonButtonStyles = {
  minWidth: '160px',
  height: '48px',
  borderRadius: '8px',
  fontSize: '1rem',
  fontWeight: 'medium',
  textTransform: 'none',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  },
  '& .MuiButton-endIcon': {
    marginRight: '8px',
    marginLeft: '-4px',
  },
  '@media (max-width: 600px)': {
    minWidth: 'auto',
    height: '36px',
    fontSize: '0.875rem',
    padding: '6px 12px',
  }
};

const MortgageTab = ({
  formData,
  handleChange,
  handleSubmit,
  loading,
  loadingMortgage,
  mortgageErrorMsg,
  mortgageError,
  isEditMode,
  toggleEditMode,
  banks,
  trackTypes,
  handleTrackChange,
  addTrack,
  removeTrack,
  calculateWeightedInterest,
  isMobile
}) => {
  return (
    <form onSubmit={handleSubmit}>
      {loadingMortgage ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
          <CircularProgress />
        </Box>
      ) : mortgageErrorMsg ? (
        <NotificationAlert
          type="error"
          message={mortgageErrorMsg}
          onClose={() => {}}
        />
      ) : (
        <>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 3,
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 0 }
          }}>
            <SectionTitle sx={{ mb: 0 }}>
              <AccountBalanceIcon sx={{ ml: 1.5, verticalAlign: 'middle', color: 'primary.main' }} />
              פרטי המשכנתא
            </SectionTitle>
            
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              width: { xs: '100%', sm: 'auto' },
              justifyContent: { xs: 'center', sm: 'flex-start' }
            }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  ml: 2, 
                  mr: 2, 
                  fontWeight: 'medium',
                  color: isEditMode ? 'warning.main' : 'text.secondary'
                }}
              >
                {isEditMode ? 'מצב עריכה' : 'מצב צפייה'}
              </Typography>
              <Tooltip title={isEditMode ? "חזור למצב צפייה" : "עבור למצב עריכה"}>
                <Button 
                  variant={isEditMode ? "outlined" : "contained"}
                  color={isEditMode ? "warning" : "primary"}
                  onClick={toggleEditMode}
                  endIcon={<EditIcon />}
                  sx={{
                    ...commonButtonStyles,
                    minWidth: { xs: '100%', sm: '140px' },
                    height: '40px',
                  }}
                >
                  {isEditMode ? 'בטל עריכה' : 'ערוך נתונים'}
                </Button>
              </Tooltip>
            </Box>
          </Box>

          {!isEditMode ? (
            <MortgageView 
              formData={formData}
              banks={banks}
              trackTypes={trackTypes}
              calculateWeightedInterest={calculateWeightedInterest}
              onSetupMortgage={toggleEditMode}
              isLoading={loadingMortgage}
              error={mortgageError}
            />
          ) : (
            <>
              <MortgageForm 
                formData={formData}
                handleChange={handleChange}
                handleTrackChange={handleTrackChange}
                addTrack={addTrack}
                removeTrack={removeTrack}
                banks={banks}
                trackTypes={trackTypes}
                calculateWeightedInterest={calculateWeightedInterest}
                isMobile={isMobile}
              />

              <SectionTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AttachMoneyIcon sx={{ ml: 1.5, verticalAlign: 'middle', color: 'primary.main' }} />
                  מסלולי משכנתא
                </Box>
                <Chip 
                  label={`סה"כ מסלולים: ${formData.mortgageTracks.length}`} 
                  size="small" 
                  color="primary" 
                  variant="outlined"
                  sx={{ fontWeight: 'medium' }}
                />
              </SectionTitle>

              {formData.mortgageTracks.map((track, index) => (
                <MortgageTrack
                  key={index}
                  track={track}
                  index={index}
                  trackTypes={trackTypes}
                  handleTrackChange={handleTrackChange}
                  removeTrack={removeTrack}
                  canDelete={formData.mortgageTracks.length > 1}
                />
              ))}

              <Button
                variant="outlined" 
                color="primary" 
                onClick={addTrack}
                endIcon={<AddIcon />}
                sx={{
                  ...commonButtonStyles,
                  mt: 3,
                  mb: 4,
                  width: isMobile ? '100%' : 'auto',
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2,
                  }
                }}
              >
                הוסף מסלול חדש
              </Button>

              <ButtonContainer>
                <Stack 
                  direction={{ xs: 'column', sm: 'row' }} 
                  spacing={{ xs: 1, sm: 6 }}
                  justifyContent="center"
                  alignItems="center"
                  sx={{ 
                    width: '100%', 
                    maxWidth: '800px', 
                    mx: 'auto',
                    mt: 4,
                    mb: 2,
                    px: { xs: 1, sm: 4 }
                  }}
                >
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={toggleEditMode}
                    endIcon={<CancelIcon />}
                    disabled={loading}
                    sx={{
                      ...commonButtonStyles,
                      flex: { sm: '0 1 240px' },
                      minWidth: { xs: 'auto', sm: '240px' },
                      width: { xs: '100%', sm: 'auto' },
                      borderWidth: 2,
                      '&:hover': {
                        borderWidth: 2,
                      }
                    }}
                  >
                    בטל שינויים
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    endIcon={loading ? null : <CheckCircleIcon />}
                    sx={{
                      ...commonButtonStyles,
                      flex: { sm: '0 1 240px' },
                      minWidth: { xs: 'auto', sm: '240px' },
                      width: { xs: '100%', sm: 'auto' },
                      bgcolor: 'primary.main',
                      '&:hover': {
                        bgcolor: 'primary.dark',
                        boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                      }
                    }}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : "שמור שינויים"}
                  </Button>
                </Stack>
              </ButtonContainer>
            </>
          )}
        </>
      )}
    </form>
  );
};

export default MortgageTab; 